import { useState } from 'react';
import { useNavigate } from "react-router-dom";
import useDebounce from "../../../core/hooks/useDebounce.js";
import quizManagementService from "./quizManagementService.js";
import { useMutation, useQuery } from 'react-query';
import useAuth from '../../../core/hooks/useAuth.js';
const { DateTime } = require("luxon");

const useQuizManagement = (props) => {
    // State variables
    const [pageOptions, setPageOptions] = useState({ page: 1, take: 20 });
    const [filterOptions, setFilterOptions] = useState({ searchText: "" });
    const [searchText, setSearchText] = useState("");
    const { authenticatedUser } = useAuth();


    // Function to update search text and trigger debouncing
    const getSearchData = (text) => {
        setFilterOptions({ ...filterOptions, searchText: text });
        setSearchText(text);
    };

    // Function to update filter options and trigger data refetch
    const getFilterData = async (options, fields) => {
        if (options && fields) {
            await setFilterOptions({ ...filterOptions, ...options, fields });
        } else {
            await setFilterOptions({ searchText: filterOptions.searchText });
        }
        refetch();
    };

    // Function to handle pagination change
    const onChange = (pagination) => {
        setPageOptions({ ...pageOptions, page: pagination.current, take: pagination.pageSize });
    };

    // Custom hook for debouncing search text
    const { debouncedValue } = useDebounce(searchText, 1000);

    // Query to fetch data from the server
    const {
        data,
        isLoading,
        isFetching,
        error,
        refetch,
    } = useQuery(
        ["formsData", debouncedValue, pageOptions],
        () => quizManagementService.getForms({ pageOptions: JSON.stringify({ ...pageOptions }), filterOptions: JSON.stringify({ ...filterOptions }) }),
        {
            initialData: {
                data: { data: [], meta: { hasPrevious: false, hasNext: false } },
            },
            keepPreviousData: true,
            refetchOnWindowFocus: false,
        }
    );

    // Navigation hook
    const navigate = useNavigate();

    // Placeholder update function
    const updateFn = (id) => { 
        navigate(`/quizUpdate/${id}`)
    }

    // Function to format date using Luxon
    const formatDate = (date) => {
        if (date) {
            const inputDate = DateTime.fromISO(date);
            return inputDate.toFormat("yyyy-MM-dd");
        } else {
            return "";
        }
    }

    // Function to prepare data for rendering
    const dataPreparation = (dataArray) => {
        return dataArray?.map((data) => ({
            id: data.id,
            courseName: data?.course?.title,
            quizName: data?.formName,
            status: data?.status,
            createdAt: formatDate(data?.createdAt),
            numberOfQuestions: data?.fields?.length,
        }));
    };

    // Function to navigate to quiz creation page
    const navigateToQuizCreation = () => {
        navigate(`/quizCreation`);
    };
    
    // Function to navigate topreview quiz page
    const previewFn = (id) => {    
        navigate(`/quizPreview/${id}`);
    }

    // Mutation hook for updating form data
    const updateForm = useMutation({
        mutationFn: async (formData) => quizManagementService.updateForm(formData),
        onSuccess: async (data, variables, context) => {
            refetch();
        },
        onError: async (error) => { console.log(error) },
    });

    // Function to handle switching form status
    const switcherFn = (form) => {
        const data={ id:form.id,status:form.status,updatedBy:authenticatedUser.id,updatedAt:DateTime.now()} ;
        updateForm.mutate(data);
    }



    // Function to handle form deletion
    const deleteFn = (id) => {
        const data={ id:id,deleted:true,updatedBy:authenticatedUser.id,updatedAt:DateTime.now()} ;

         updateForm.mutate(data) 
        }

    return {
        deleteFn,
        updateFn,
        data: { data: dataPreparation(data.data.data), meta: data.data.meta },
        isFetching,
        navigateToQuizCreation,
        onChange,
        getSearchData,
        getFilterData,
        switcherFn,
        previewFn
    };
};

export default useQuizManagement;
